import { User, UserRole } from "../../../api/Users";
import {
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
} from "@reduxjs/toolkit";
import { fetchUsersByRoles } from "../../configs/logic/Thunks";
import { RootState } from "../../../../src/app/store";
import { fetchCommitteeMembers, fetchConveners, fetchDealingOfficers, fetchForwardingAuthorities, fetchIOAppointingAuthorities, fetchUsersWithEmailIds, fetchUsersWithUserIds } from "./thunk";

const userEntityAdapter = createEntityAdapter<User, string>({
  selectId: (user) => user.user_id,
});

const resetUserDerivedState = (state: UsersState) => {
  state.investigators = [];
  state.collaborators = [];
  state.supervising_authorities = [];
  state.conveners = [];
  state.committee_members = [];
  state.io_appointing_authorities= []
  state.emailIdMap = {};
};

interface UsersState {
  users: EntityState<User, string>;
  emailIdMap: { [key: string]: string };
  investigators: string[];
  collaborators: string[];
  supervising_authorities: string[];
  conveners: string[];
  committee_members: string[];
  dealing_officer: string[];
  io_appointing_authorities: string[];
  forwarding_authorities: string[];
}

const initialState: UsersState = {
  users: userEntityAdapter.getInitialState(),
  emailIdMap: {},
  investigators: [],
  collaborators: [],
  supervising_authorities: [],
  conveners: [],
  committee_members: [],
  dealing_officer: [],
  io_appointing_authorities: [],
  forwarding_authorities: [],
};

const usersSlice = createSlice({
  name: "usersSlice",
  initialState,
  reducers: {},
  extraReducers: (builder) => {

    builder.addCase(fetchUsersByRoles.fulfilled, (state, action) => {
      const users = action.payload;
      userEntityAdapter.upsertMany(state.users, users);

      users.forEach((user) => {
        state.emailIdMap[user.email_id] = user.user_id;

        switch (user.role) {
          case UserRole.INVESTIGATOR:
            if (!state.investigators.includes(user.user_id)) {
               state.investigators.push(user.user_id);
            }
            break;

          case UserRole.COLLABORATOR:
            if (!state.collaborators.includes(user.user_id)) {
               state.collaborators.push(user.user_id);
            }
            break;

          case UserRole.SUPERVISING_AUTH_L1:
          case UserRole.SUPERVISING_AUTH_L2:
            if (!state.supervising_authorities.includes(user.user_id)) {
               state.supervising_authorities.push(user.user_id);
            }
            break;
          
          default:
            break;
        }
      });
    });

    builder.addCase(fetchConveners.fulfilled, (state, action) => {
      const users = action.payload;
      userEntityAdapter.upsertMany(state.users, users);
      users.forEach((user) => {
        state.emailIdMap[user.email_id] = user.user_id;

        if (!state.conveners.includes(user.user_id)) {
          state.conveners.push(user.user_id);
        }
      });
    });

    builder.addCase(fetchCommitteeMembers.fulfilled, (state, action) => {
      const users = action.payload;

      userEntityAdapter.upsertMany(state.users, users);

      users.forEach((user) => {
        state.emailIdMap[user.email_id] = user.user_id;

        if (!state.committee_members.includes(user.user_id)) {
          state.committee_members.push(user.user_id);
        }
      });
    });

    builder.addCase(fetchUsersWithUserIds.fulfilled, (state, action) => {
      const users = action.payload;
      userEntityAdapter.upsertMany(state.users, users);
      users.forEach((user) => {
        state.emailIdMap[user.email_id] = user.user_id;
      });
    });

    builder.addCase(fetchUsersWithEmailIds.fulfilled, (state, action) => {
      const users = action.payload;
      userEntityAdapter.upsertMany(state.users, users);
      users.forEach((user) => {
        state.emailIdMap[user.email_id] = user.user_id;
      });
    });

    builder.addCase(fetchIOAppointingAuthorities.fulfilled, (state, action) => {
      const users = action.payload;
      userEntityAdapter.upsertMany(state.users, users);
      users.forEach((user) => {
        state.emailIdMap[user.email_id] = user.user_id;

        if (!state.io_appointing_authorities.includes(user.user_id)) {
          state.io_appointing_authorities.push(user.user_id);
        }
      });
    });


    builder.addCase(fetchDealingOfficers.fulfilled, (state, action) => {
      const users = action.payload;
      userEntityAdapter.upsertMany(state.users, users);
      users.forEach((user) => {
        state.emailIdMap[user.email_id] = user.user_id;

        if (!state.dealing_officer.includes(user.user_id)) {
          state.dealing_officer.push(user.user_id);
        }
      });
    });

    builder.addCase(fetchForwardingAuthorities.fulfilled, (state, action) => {
      const users = action.payload;
      userEntityAdapter.upsertMany(state.users, users);
      users.forEach((user) => {
        state.emailIdMap[user.email_id] = user.user_id;

        if (!state.forwarding_authorities.includes(user.user_id)) {
          state.forwarding_authorities.push(user.user_id);
        }
      });
    });

  }
});

const userSelectors = userEntityAdapter.getSelectors<RootState>(
  (state) => state.users.users
);

const selectInvestigatorIds = (state: RootState) => state.users.investigators;

const selectCollaboratorIds = (state: RootState) => state.users.collaborators;

const selectSupervisingAuthorityIds = (state: RootState) =>
  state.users.supervising_authorities;

const selectConvenerIds = (state: RootState) => state.users.conveners;

const selectCommitteeMemberIds = (state: RootState) =>
  state.users.committee_members;

const selectForwardingAuthorityIds = (state: RootState) =>
  state.users.forwarding_authorities;

export const getInvestigators = createSelector(
  [selectInvestigatorIds, userSelectors.selectEntities],
  (ids, entities) => ids.map((id) => entities[id]).filter(Boolean)
);

export const getCollaborators = createSelector(
  [selectCollaboratorIds, userSelectors.selectEntities],
  (ids, entities) => ids.map((id) => entities[id]).filter(Boolean)
);

export const getSupervisingAuthorityUsers = createSelector(
  [selectSupervisingAuthorityIds, userSelectors.selectEntities],
  (ids, entities) => ids.map((id) => entities[id]).filter(Boolean)
);

export const getConveners = createSelector(
  [selectConvenerIds, userSelectors.selectEntities],
  (ids, entities) => ids.map((id) => entities[id]).filter(Boolean)
);

export const getCommitteeMembers = createSelector(
  [selectCommitteeMemberIds, userSelectors.selectEntities],
  (ids, entities) => ids.map((id) => entities[id]).filter(Boolean)
);

export const getForwardingAuthorities = createSelector(
  [selectForwardingAuthorityIds, userSelectors.selectEntities],
  (ids, entities) => ids.map((id) => entities[id]).filter(Boolean)
);

// TODO: update to select reviewers
export const getReviewers = createSelector(
  [getInvestigators],
  (investigators) => investigators
);

export const getUserById = userSelectors.selectById;

export const getUserByEmailId = (emailId: string) =>
  createSelector(
    [
      (state: RootState) => state.users.emailIdMap,
      userSelectors.selectEntities
    ],
    (emailMap, entities) => {
      const userId = emailMap[emailId];
      return userId ? entities[userId] : undefined;
    }
  );


export const getUsersByEmailIds = createSelector(
  [
    (state: RootState) => state.users.emailIdMap,
    userSelectors.selectEntities,
    (_: RootState, emailIds: string[]) => emailIds,
  ],
  (emailMap, entities, emailIds) => {
    const result: Record<string, User> = {};

    emailIds.forEach((email) => {
      const userId = emailMap[email];
      if (userId) {
        const user = entities[userId];
        if (user) {
          result[email] = user;
        }
      }
    });

    return result;
  }
);

export const getUsersByIds = createSelector(
  [
    userSelectors.selectEntities,
    (_: RootState, userIds: string[]) => userIds,
  ],
  (entities, userIds) => {

    const result : Record<string, User> = {};

    userIds.forEach((userId) => {
      const user = entities[userId];
      if (user) {
        result[userId] = user;
      }
    })

    return result;
  }
);

const selectIOAppointingAuthorityIds = (state: RootState) =>
  state.users.io_appointing_authorities;

export const getIOAppointingAuthorities = createSelector(
  [selectIOAppointingAuthorityIds, userSelectors.selectEntities],
  (ids, entities) => ids.map((id) => entities[id]).filter(Boolean)
);

const selectDealingOfficerIds = (state: RootState) =>
  state.users.dealing_officer;

export const getDealingOfficers = createSelector(
  [selectDealingOfficerIds, userSelectors.selectEntities],
  (ids, entities) => ids.map((id) => entities[id]).filter(Boolean),
);

export const usersReducer = usersSlice.reducer;